import React from "react";

import cx from "classnames";

import { Button } from "../Button";
import { Flex } from "../Flex";
import { Spacer } from "../Grid";

import styles from "./ExpansionPanel.scss";

/**
 * A component that shows/hides a provided element by clicking their title.
 *
 * The ExpansionPanel component provides a collapsible section that can be toggled
 * open or closed. It's useful for organizing content in an expandable/collapsible
 * way, helping to manage space and improve readability.
 *
 * Features:
 * - Customizable title and content
 * - Configurable title alignment
 * - Accessible button controls
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = React.useState(false);
 *
 * <ExpansionPanel
 *   title="Settings"
 *   isOpen={isOpen}
 *   setIsOpen={() => setIsOpen(!isOpen)}
 *   content={<div>Panel content here...</div>}
 *   titleAlignment="center"
 * />
 * ```
 */
export interface ExpansionPanelProps {
  /**
   * Element you want rendered in the opened expansion panel
   * @required
   */
  content: React.ReactNode;

  /**
   * Defines the state for the panel
   * @required
   */
  isOpen: boolean;

  /**
   * When the title is clicked, we invoke this function
   * @required
   */
  setIsOpen: () => void;

  /**
   * Expansion panel's title
   * @required
   */
  title: string;

  /**
   * How to align the title horizontally
   * @default "flex-start"
   */
  titleAlignment?: "center" | "flex-start" | "flex-end";
}

/**
 * ExpansionPanel component that provides a collapsible section with a title.
 * The component is designed to be simple to use while providing flexibility
 * in terms of content and styling.
 */
export function ExpansionPanel({
  isOpen,
  setIsOpen,
  title,
  titleAlignment = "flex-start",
  content,
}: ExpansionPanelProps) {
  return (
    <div className={styles["full-width"]}>
      <Flex justifyContent={titleAlignment}>
        <Button
          variant="link"
          icon={isOpen ? "caret_up" : "caret_dn"}
          type="button"
          iconAlignment="right"
          data-testid="expansion-button"
          onClick={() => setIsOpen()}
        >
          {title}
        </Button>
      </Flex>
      <div
        className={cx(styles["expansion-panel"], {
          [styles["expansion-panel-open"]]: isOpen,
        })}
        data-testid={`displayed-content-${isOpen}`}
      >
        <Spacer size="small" />
        {content}
      </div>
    </div>
  );
}
